import React, { useRef } from 'react'
import { createPortal } from 'react-dom'
import useOutsideClick from '../hooks/useClickOutside'
import Divider from './Divider'
import Button from 'antd/lib/button'

// The page is dimmed exactly once no matter how many overlays are stacked:
// the bottom-most mask paints the dark backdrop and every mask stacked above
// it is transparent. Done in CSS (masks are portaled siblings under
// document.body) so the browser keeps it correct as overlays mount and
// unmount — JS coordination here (DOM queries or a registry, both tried)
// raced with React commits and could leave no backdrop at all.
const MASK_STYLE_ID = 'overlay-mask-backdrop-style'
if (typeof document !== 'undefined' && !document.getElementById(MASK_STYLE_ID)) {
    const styleTag = document.createElement('style')
    styleTag.id = MASK_STYLE_ID
    styleTag.textContent = `
        [data-overlay-mask] { background-color: rgba(0, 0, 0, 0.45); }
        [data-overlay-mask] ~ [data-overlay-mask] { background-color: transparent; }
    `
    document.head.appendChild(styleTag)
}

const styles = {
    mask: {
        position: 'fixed',
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        zIndex: 2000,
        height: '100%',
        // backgroundColor comes from the [data-overlay-mask] stylesheet above,
        // so stacked masks can be made transparent — an inline value here
        // would override it.
        overflow: 'auto',
        pointerEvents: 'auto'
    },
    container: {
        maxWidth: '95%',
        maxHeight: '95vh',
        width: 'auto',
        minWidth: '400px',
        padding: 0,
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        backgroundColor: 'white',
        borderRadius: 8,
        isolation: 'isolate',
        display: 'flex',
        flexDirection: 'column',
        pointerEvents: 'auto',
        boxShadow: '0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 9px 28px 8px rgba(0, 0, 0, 0.05)',
        overflow: 'hidden'
    },
    closeButton: {
        position: 'absolute',
        top: 15,
        right: 35,
        cursor: 'pointer',
        fontSize: 22,
        zIndex: 1,
    },
    header: {
        backgroundColor: '#fafafa',
        fontSize: 22,
        padding: '0.75rem 1.5rem',
        borderTopLeftRadius: 8,
        borderTopRightRadius: 8
    },
    plainHeader: {
        backgroundColor: 'white',
        fontSize: 14,
        padding: '0.4rem 1.25rem',
        borderTopLeftRadius: 8,
        borderTopRightRadius: 8,
        color: '#333'
    },
    smallHeader: {
        backgroundColor: 'white',
        fontSize: 14,
        fontWeight: 'normal',
        padding: '0.4rem 1.5rem',
        borderTopLeftRadius: 8,
        borderTopRightRadius: 8
    },
    footer: {
        backgroundColor: '#fafafa',
        fontSize: 32,
        borderTop: '1px solid #d8d8d8',
        borderBottomLeftRadius: 10,
        borderBottomRightRadius: 10,
        width: '100%',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '0.16em 1em'
    },
    userDetailsFooter: {
        backgroundColor: '#fafafa',
        fontSize: 32,
        borderTop: '1px solid #d8d8d8',
        borderBottomLeftRadius: 10,
        borderBottomRightRadius: 10,
        width: '100%',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '0.75rem 1.5rem'
    },
    content: {
        flex: 1,
        overflow: 'auto',
        minHeight: 0
    }
}

const Overlay = ({ children, visible, onClose, minimal, headerText, closebutton, footer, hideOk, onOK, okText, background, zIndex, plainHeader, smallHeader, overlayType, width, userDetailsFooter, disableOutsideClick, overflowVisible }) => {
    const container = useRef()

    // Only use outside click when the modal is closable (has close button or footer)
    const isClosable = closebutton || footer
    const shouldUseOutsideClick = !disableOutsideClick && isClosable
    useOutsideClick(container, shouldUseOutsideClick ? () => onClose() : () => { })

    if (!visible) return null

    let maskStyle = { ...styles.mask, zIndex: zIndex || 2000 }
    let containerStyle = { ...styles.container, backgroundColor: background || '#fff' }

    // Opt-in: let content (e.g. custom dropdowns) extend past the modal edge
    // instead of being clipped/scrolled inside it.
    if (overflowVisible) containerStyle.overflow = 'visible'

    // Set width based on prop or content-based sizing
    if (width) {
        containerStyle.width = width;
        containerStyle.minWidth = 'unset';
    } else {
        // Default to auto width and let content determine size
        containerStyle.width = 'auto';
        containerStyle.boxSizing = 'border-box';
    }

    // Determine which header style to use
    let headerStyle = styles.header
    if (smallHeader) {
        headerStyle = styles.smallHeader
    } else if (plainHeader) {
        headerStyle = styles.plainHeader
    }

    const overlayContent = (
        <div
            style={maskStyle}
            data-overlay-mask
            onMouseDown={(e) => {
                // Only stop propagation if this is the highest z-index modal
                if (e.target === e.currentTarget) {
                    e.stopPropagation()
                }
            }}
            onClick={(e) => {
                // Only stop propagation if this is the highest z-index modal
                if (e.target === e.currentTarget) {
                    e.stopPropagation()
                }
            }}
        >
            <div
                ref={container}
                style={containerStyle}
                onClick={(event) => event.stopPropagation()}
                onMouseDown={(event) => event.stopPropagation()}
            >
                {!minimal && (
                    <>
                        <div style={headerStyle}>
                            {headerText}
                            {closebutton && (
                                <div style={styles.closeButton}>
                                    <span onClick={(e) => {
                                        e.stopPropagation();
                                        onClose();
                                    }}>X</span>
                                </div>
                            )}
                        </div>
                        {!plainHeader && !smallHeader && <Divider />}
                    </>
                )}
                <div style={overflowVisible ? { ...styles.content, overflow: 'visible' } : styles.content}>
                    {children}
                </div>
                {footer && (
                    <div style={userDetailsFooter ? styles.userDetailsFooter : styles.footer}>
                        <div style={userDetailsFooter ? { display: 'flex', alignItems: 'center', height: '100%' } : {}}>
                            <Button onClick={(e) => {
                                e.stopPropagation();
                                onClose();
                            }}>Cancel</Button>
                        </div>
                        <div style={userDetailsFooter ? { marginLeft: 'auto', marginRight: '-8px', display: 'flex', alignItems: 'center', height: '100%' } : { marginLeft: 'auto', marginRight: '-8px' }}>
                            {!hideOk && <Button onClick={(e) => {
                                e.stopPropagation();
                                onOK();
                            }} type='primary'>{okText ? okText : "Save"}</Button>}
                        </div>
                    </div>
                )}
            </div>
        </div>
    )

    return createPortal(overlayContent, document.body)
}

export default Overlay
